import { useMutation } from '@apollo/client/react';
import { Alert, Button, Form, Input, Modal, Select } from 'antd';
import styled from '@emotion/styled';
import dayjs from 'dayjs';

import SectionMessage from 'components/SectionMessage/SectionMessage';
import BlockedPhoneHistoryTable from './BlockedPhoneHistoryTable';

import { BLOCK_PHONE_NUMBER } from 'mutations/blockPhone';

import useTimeoutValue from 'hooks/useTimeoutValue';
import { formatDate, formatDateToSubmit } from 'utilities/datesAndTimes';
import { formatPhoneNumber } from 'utilities/helpers';
import { BREAKPOINT, SIZE, STATUS } from 'utilities/constants';
import { REASON_CODES, BLOCK_END_OPTIONS } from './constants';

const { TextArea } = Input;

const FormGrid = styled.div`
  @media (min-width: ${BREAKPOINT.SM_PX}) {
    display: grid;
    grid-gap: 0 var(--spacing-md);
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-areas:
      'phoneNumber customerId reasonCode blockEnd'
      'reasonDescription reasonDescription reasonDescription reasonDescription';
  }
`;

const FormItem = styled(Form.Item)`
  grid-area: ${(props) => props.name};
`;

const BlockPhoneForm = ({ onEdit }) => {
  const [form] = Form.useForm();
  const [editSuccess, setEditSuccess] = useTimeoutValue(false, 4000);
  const [submissionSuccess, setSubmissionSuccess] = useTimeoutValue(
    false,
    4000,
  );

  const [blockPhoneNumber, blockMutation] = useMutation(BLOCK_PHONE_NUMBER, {
    onCompleted: () => {
      setSubmissionSuccess(true);
      form.resetFields();
    },
    refetchQueries: ['BlockedPhoneNumberHistory'],
  });

  const getSuccessMessage = () => {
    const data = blockMutation.data?.blockPhoneNumber;
    const formattedPhone = formatPhoneNumber(data?.phoneNumber);

    if (data?.blockEnd) {
      return `Phone number ${formattedPhone} is blocked until ${formatDate(data?.blockEnd, true)}`;
    }

    return `Phone number ${formattedPhone} is permanently blocked`;
  };

  const handleSubmit = (values) => {
    const { phoneNumber, blockEnd, reasonCode, reasonDescription, customerId } =
      values;

    const formattedPhone = formatPhoneNumber(phoneNumber);

    const durationText =
      blockEnd === '3_DAYS'
        ? `Block ${formattedPhone} for 3 days?`
        : `Permanently block ${formattedPhone}?`;

    Modal.confirm({
      title: 'Confirm Block',
      content: durationText,
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        // Convert block end dropdown to date
        // If permanent, blockEndDate stays null
        let blockEndDate = null;
        if (blockEnd === '3_DAYS') {
          blockEndDate = formatDateToSubmit(dayjs().add(3, 'day'), true);
        }

        await blockPhoneNumber({
          variables: {
            phoneNumber,
            reasonCode,
            reasonDescription,
            blockEnd: blockEndDate,
            customerIds: customerId ? [customerId] : null,
          },
        });
      },
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      {blockMutation.data?.blockPhoneNumber?.alreadyBlocked && !editSuccess ? (
        <>
          <Alert
            banner
            type="warning"
            message="This phone number is already blocked. Details of the existing block:"
            style={{ marginBottom: 'var(--spacing-sm)' }}
            data-testid="duplicate-phone-alert"
          />
          <BlockedPhoneHistoryTable
            data={[blockMutation.data.blockPhoneNumber]}
            loading={false}
            onEdit={(record) =>
              onEdit(record, () => {
                setEditSuccess(true);
                blockMutation.reset();
              })
            }
            hidePagination
            hideActions={false}
            style={{ marginBottom: 'var(--spacing-sm)' }}
          />
        </>
      ) : (
        <>
          <SectionMessage
            size={SIZE.SM}
            status={STATUS.SUCCESS}
            text="Blocked phone number updated"
            cover={true}
            visible={editSuccess}
          />
          <SectionMessage
            size={SIZE.SM}
            status={STATUS.SUCCESS}
            text={getSuccessMessage()}
            cover={true}
            visible={submissionSuccess}
          />
        </>
      )}

      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <FormGrid>
          <FormItem
            label="Phone Number"
            name="phoneNumber"
            rules={[
              { required: true, message: 'Phone number is required' },
              {
                pattern: /^\d{10}$/,
                message: 'Must be 10 digits without country code',
              },
            ]}
          >
            <Input
              prefix="+1"
              placeholder="2025551234"
              maxLength={10}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/\D/g, '');
                form.setFieldValue('phoneNumber', cleaned);
              }}
            />
          </FormItem>

          <FormItem label="Customer ID" name="customerId">
            <Input placeholder="Optional" />
          </FormItem>

          <FormItem
            label="Reason Code"
            name="reasonCode"
            rules={[{ required: true, message: 'Reason code is required' }]}
          >
            <Select placeholder="Select a reason" options={REASON_CODES} />
          </FormItem>

          <FormItem
            label="Block End Date"
            name="blockEnd"
            rules={[{ required: true, message: 'Block end date is required' }]}
          >
            <Select
              placeholder="Select a duration"
              options={BLOCK_END_OPTIONS}
            />
          </FormItem>

          <FormItem label="Reason Description" name="reasonDescription">
            <TextArea
              rows={3}
              placeholder="Provide a detailed reason for blocking this number"
            />
          </FormItem>
        </FormGrid>

        {blockMutation.error && (
          <Alert
            banner
            type="error"
            title="Failed to block phone number. Please try again."
          />
        )}

        <Button
          type="primary"
          htmlType="submit"
          loading={blockMutation.loading}
          disabled={blockMutation.loading || submissionSuccess}
          data-testid="block-phone-submit-button"
        >
          Block Phone Number
        </Button>
      </Form>
    </div>
  );
};

export default BlockPhoneForm;
